import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import Accordion from '../../../shared/Accordion';
import CancelButton from '../../../shared/CancelButton';
import { usePermissions } from '../../../shared/PermissionsContext';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { formatDateTime } from '../../../shared/tenantClock';
import { QuickBooksConnectionNotice } from '../../../shared/QuickBooksItemPicker';
import { QuickBooksCustomerPicker, QuickBooksIssuePills, QuickBooksDifferences, customerLabel } from '../../../shared/QuickBooksCustomerPicker';
import { askConfirm } from '../../../shared/AppDialogs';

// The Customer page's QuickBooks card (Link Customers to QuickBooks
// Customers): the QuickBooks Customer this Customer is linked to, and
// linking, creating, changing or removing it — each saved straight away,
// apart from the page's own edits. Shown on any record holding the
// Customer relationship, and on one still linked after it stopped being
// one. QuickBooks Customers are only fetched when the picker is opened.
// Backend: quickbooksCustomerLinkService.js.

const SMALL_PRIMARY = { padding: '5px 12px', border: 'none', borderRadius: 6, background: '#1976d2', color: '#fff', fontSize: 13, fontWeight: 500 };
const LINK_BUTTON = { background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 };

function CustomerQuickBooksCard({ entityId, isCustomer }) {
  const { accessFor } = usePermissions();
  const canEdit = accessFor('sales-customers').update;
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [state, setState] = useState(null);
  // null = closed; otherwise what /api/quickbooks/customers/pickable said.
  const [picker, setPicker] = useState(null);
  // The QuickBooks Customer chosen, with how it differs, awaiting confirm.
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`/api/quickbooks/customers/${entityId}`);
      setState(res.data.data);
    } catch {
      setState(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [entityId]);

  const act = async (fn, failMessage) => {
    setBusy(true);
    try {
      await fn();
      dismissError();
      await load();
    } catch (err) {
      showError(err, failMessage);
    }
    setBusy(false);
  };

  const closeAll = () => { setPicker(null); setConfirming(null); };

  const openPicker = () => act(async () => {
    const res = await axios.get('/api/quickbooks/customers/pickable');
    setPicker(res.data.data);
    setConfirming(null);
  }, 'Couldn’t load QuickBooks Customers');

  const choose = (customer) => customer && act(async () => {
    const res = await axios.get(`/api/quickbooks/customers/${entityId}/compare`, { params: { qbo_customer_id: customer.id } });
    setConfirming({ customer, differences: res.data.data.differences });
  }, 'Couldn’t compare with QuickBooks');

  const link = () => act(async () => {
    await axios.put(`/api/quickbooks/customers/${entityId}`, { qbo_customer_id: confirming.customer.id });
    closeAll();
  }, 'Linking failed');

  const create = async () => {
    if (!(await askConfirm('Create this Customer in QuickBooks from Optim’s details, and link it?', { confirmLabel: 'Create and Link' }))) return;
    act(async () => {
      await axios.post(`/api/quickbooks/customers/${entityId}/create`);
      closeAll();
    }, 'Creating in QuickBooks failed');
  };

  const retry = () => act(() => axios.post(`/api/quickbooks/customers/${entityId}/retry`), 'Sending to QuickBooks failed');

  const remove = async () => {
    if (!(await askConfirm(`Remove this Customer’s QuickBooks link?\n\nIt’s linked to “${state.link.qbo_customer_name}”. Nothing changes in QuickBooks, and Optim’s changes stop going to it.`, { confirmLabel: 'Remove', destructive: true }))) return;
    act(() => axios.delete(`/api/quickbooks/customers/${entityId}`), 'Removing the link failed');
  };

  const link_ = state?.link;
  if (!state || (!isCustomer && !link_)) return null;

  let body;
  if (picker) {
    if (picker.connection.status !== 'connected') {
      body = (
        <>
          <QuickBooksConnectionNotice connection={picker.connection} />
          <div style={{ marginTop: 10 }}><CancelButton onClick={closeAll} /></div>
        </>
      );
    } else if (!picker.mapping.complete) {
      body = (
        <>
          <div style={{ fontSize: 13, color: '#92400e' }}>Map the QuickBooks Customer fields first, in Accounting › QuickBooks › Customers.</div>
          <div style={{ marginTop: 10 }}><CancelButton onClick={closeAll} /></div>
        </>
      );
    } else if (confirming) {
      body = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 14 }}>{customerLabel(confirming.customer)}</div>
          <QuickBooksDifferences differences={confirming.differences} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={link} disabled={busy} title="Links this Customer and sends Optim’s details to QuickBooks" style={{ ...SMALL_PRIMARY, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: busy ? 'not-allowed' : 'pointer' }}>
              {busy && <Loader2 size={14} style={{ animation: 'qb-spin 0.8s linear infinite' }} />}
          {busy ? 'Linking...' : confirming.differences.length ? 'Link and overwrite QuickBooks' : 'Link'}
            </button>
            <CancelButton onClick={closeAll} disabled={busy} />
          </div>
        </div>
      );
    } else {
      body = (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <QuickBooksCustomerPicker customers={picker.customers} value={null} onChange={choose} autoFocus disabled={busy} />
          </div>
          <CancelButton onClick={closeAll} disabled={busy} />
        </div>
      );
    }
  } else if (link_) {
    const failed = link_.issues.some((i) => i.code === 'push_failed');
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
        <span>{link_.qbo_customer_name}</span>
        <QuickBooksIssuePills issues={link_.issues} />
        {failed && <span style={{ fontSize: 12, color: '#b91c1c' }}>{link_.push_error}</span>}
        {link_.pushed_at && <span style={{ fontSize: 12, color: '#666' }}>Last sent {formatDateTime(link_.pushed_at)}</span>}
        {canEdit && (
          <span style={{ display: 'inline-flex', gap: 12 }}>
            {failed && <button type="button" onClick={retry} disabled={busy} title="Sends this Customer’s details to QuickBooks again" style={LINK_BUTTON}>Retry</button>}
            <button type="button" onClick={openPicker} disabled={busy} title="Picks a different QuickBooks Customer for this Customer" style={LINK_BUTTON}>Change</button>
            <button type="button" onClick={remove} disabled={busy} title="Removes this Customer’s QuickBooks link" style={{ ...LINK_BUTTON, color: '#b91c1c' }}>Remove</button>
          </span>
        )}
      </div>
    );
  } else {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
        <span style={{ color: '#888' }}>Not linked to a QuickBooks Customer</span>
        {canEdit && (
          <span style={{ display: 'inline-flex', gap: 12 }}>
            <button type="button" onClick={openPicker} disabled={busy} title="Picks the QuickBooks Customer for this Customer" style={LINK_BUTTON}>Link to QuickBooks Customer</button>
            <button type="button" onClick={create} disabled={busy} title="Creates this Customer in QuickBooks and links it" style={LINK_BUTTON}>Create in QuickBooks</button>
          </span>
        )}
      </div>
    );
  }

  return (
    <Accordion title="QuickBooks" defaultOpen={false} showMenuPlaceholder={false}>
      {body}
      {busy && <Loader2 size={14} style={{ marginTop: 6, animation: 'qb-spin 0.8s linear infinite' }} />}
      <style>{'@keyframes qb-spin { to { transform: rotate(360deg); } }'}</style>
    </Accordion>
  );
}

export default CustomerQuickBooksCard;
